import React from 'react';
import { Tab, TabGroup, TabList, TabPanels, TabPanel } from '@headlessui/react';
import { isNumber } from 'lodash';
import TabLink from '@dashboard/components/TabLink';
import { useStatistics } from '../queries';
import { useStatisticsContext } from '../Dashboard';
import { formatTimestamp, formatValue } from '../dates';
import { StatMetric, StatBody, StatChart } from './StatCard';

const numberFormatter = value => Intl.NumberFormat('us').format(value).toString();

function sumArray(array = []) {
  return array.reduce((acc, item) => acc + (isNumber(item.value) ? item.value : 0), 0);
}

export default function NewSales() {
  const { unit, range } = useStatisticsContext();
  const { data: paymentsData, isLoading: isLoadingPayments } = useStatistics('numberOfPayments');
  const { data: bookingsData, isLoading: isLoadingBookings } = useStatistics('numberOfBookings');
  const { data: subscriptionsData, isLoading: isLoadingSubscriptions } =
    useStatistics('numberOfSubscriptions');
  const { data: submissionsData, isLoading: isLoadingSubmissions } =
    useStatistics('numberOfSubmissions');

  const chartPaymentsData = paymentsData?.map(({ timestamp, value }) => ({
    timestamp: formatTimestamp(timestamp, unit, range),
    Payments: formatValue(timestamp, value),
  }));

  const chartBookingsData = bookingsData?.map(({ timestamp, value }) => ({
    timestamp: formatTimestamp(timestamp, unit, range),
    Bookings: formatValue(timestamp, value),
  }));

  const chartSubscriptionsData = subscriptionsData?.map(({ timestamp, value }) => ({
    timestamp: formatTimestamp(timestamp, unit, range),
    Subscriptions: formatValue(timestamp, value),
  }));

  const chartSubmissionsData = submissionsData?.map(({ timestamp, value }) => ({
    timestamp: formatTimestamp(timestamp, unit, range),
    Submissions: formatValue(timestamp, value),
  }));

  const maxPayments = paymentsData && Math.max(...paymentsData?.map(({ value }) => value), 0);
  const minPayments = paymentsData && Math.min(...paymentsData?.map(({ value }) => value), 0);
  const maxBookings = (bookingsData && Math.max(...bookingsData.map(({ value }) => value), 0)) || 0;
  const minBookings = (bookingsData && Math.min(...bookingsData.map(({ value }) => value), 0)) || 0;
  const maxSubscriptions =
    subscriptionsData && Math.max(...subscriptionsData?.map(({ value }) => value), 0);
  const minSubscriptions =
    subscriptionsData && Math.min(...subscriptionsData?.map(({ value }) => value), 0);
  const maxSubmissions =
    (submissionsData && Math.max(...submissionsData?.map(({ value }) => value), 0)) || 0;
  const minSubmissions =
    (submissionsData && Math.min(...submissionsData?.map(({ value }) => value), 0)) || 0;

  return (
    <div>
      <TabGroup>
        <TabList className="scrollbar-hide flex gap-x-4 w-full mb-2 overflow-x-auto">
          <Tab as="div" className="outline-none cursor-pointer">
            {({ selected }) => (
              <TabLink selected={selected}>
                <StatMetric
                  isLoading={isLoadingPayments}
                  label="Payments"
                  value={numberFormatter(sumArray(paymentsData))}
                />
              </TabLink>
            )}
          </Tab>

          <Tab as="div" className="outline-none cursor-pointer">
            {({ selected }) => (
              <TabLink selected={selected}>
                <StatMetric
                  isLoading={isLoadingSubscriptions}
                  label="Subscriptions"
                  value={numberFormatter(sumArray(subscriptionsData))}
                />
              </TabLink>
            )}
          </Tab>

          <Tab as="div" className="outline-none cursor-pointer">
            {({ selected }) => (
              <TabLink selected={selected}>
                <StatMetric
                  isLoading={isLoadingBookings}
                  label="Bookings"
                  value={numberFormatter(sumArray(bookingsData))}
                />
              </TabLink>
            )}
          </Tab>

          <Tab as="div" className="outline-none cursor-pointer">
            {({ selected }) => (
              <TabLink selected={selected}>
                <StatMetric
                  isLoading={isLoadingSubmissions}
                  label="Submissions"
                  value={numberFormatter(sumArray(submissionsData))}
                />
              </TabLink>
            )}
          </Tab>
        </TabList>

        <StatBody>
          <TabPanels>
            <TabPanel>
              <StatChart
                isLoading={isLoadingPayments}
                data={chartPaymentsData}
                categories={['Payments']}
                maxValue={maxPayments}
                minValue={minPayments}
              />
            </TabPanel>

            <TabPanel>
              <StatChart
                isLoading={isLoadingSubscriptions}
                data={chartSubscriptionsData}
                categories={['Subscriptions']}
                maxValue={maxSubscriptions}
                minValue={minSubscriptions}
              />
            </TabPanel>

            <TabPanel>
              <StatChart
                isLoading={isLoadingBookings}
                data={chartBookingsData}
                categories={['Bookings']}
                maxValue={maxBookings}
                minValue={minBookings}
              />
            </TabPanel>

            <TabPanel>
              <StatChart
                isLoading={isLoadingSubmissions}
                data={chartSubmissionsData}
                categories={['Submissions']}
                maxValue={maxSubmissions}
                minValue={minSubmissions}
              />
            </TabPanel>
          </TabPanels>
        </StatBody>
      </TabGroup>
    </div>
  );
}
